import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api/client';
import { ConfirmDialog } from '../../components/ConfirmDialog';

/**
 * Pulling a v1 account's records into a v2 account, by hand.
 *
 * The migration that runs at first login looks the customer up by their own email, which silently finds
 * nothing for anyone who signed up under a NEW address — their v1 history stays where it is with no way to
 * reach it. This aims the same job at a named legacy address instead.
 *
 * Always preview first. The dry run reads and counts without writing anything, and it is the only thing
 * that can tell an operator they have typed the wrong address before a stranger's transactions land in
 * somebody's account.
 */

interface Counts {
  transactions: number;
  locations: number;
  devicesSkipped: number;
  credits: number;
  subscriptions: number;
}
interface CustomerSummary {
  customerId: string;
  email: string | null;
  name: string | null;
  deleted: boolean;
  created: number | null;
  subscriptions: { subscriptionId: string; status: string; planName: string | null; interval: string | null }[];
}
interface DryRun {
  migrated: boolean;
  dryRun: boolean;
  counts: Counts;
  message?: string;
  /** Which identifier found the v1 user. Anything but 'email' means their v1 addresses disagree. */
  matchedOn?: string;
  /** The email on the v1 User record, which is not always the one they sign in with. */
  legacyEmail?: string | null;
  warnings?: string[];
  email: string;
  userId: string;
  userEmail?: string;
  stripe: { current: CustomerSummary | null; legacy: CustomerSummary | null; twoCustomers: boolean };
}
interface LedgerRow {
  userId: string | null;
  legacyUserId: string | null;
  legacyEmail: string | null;
  state: string | null;
  counts: Counts | null;
  completedAt: string | null;
  warnings: string[] | null;
}

const LABELS: [keyof Counts, string][] = [
  ['transactions', 'Transactions'],
  ['subscriptions', 'Subscriptions'],
  ['credits', 'Credits'],
  ['locations', 'Locations'],
  ['devicesSkipped', 'Devices (not migrated)'],
];

/** Everything a live run would write. Devices are counted but deliberately never carried across. */
const movesData = (c: Counts) => c.transactions + c.locations + c.credits + c.subscriptions;

export function MigrateLegacyPanel({ accountId, accountName }: { accountId: string; accountName: string }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  /*
   * Alternatives to the email. A v1 customer can have one address on their Cognito login and another on their
   * User record; matching on email alone left those customers with an empty account, so staff need a way in that
   * does not depend on the two agreeing.
   */
  const [altOpen, setAltOpen] = useState(false);
  const [cognitoId, setCognitoId] = useState('');
  const [stripeCustomerId, setStripeCustomerId] = useState('');
  const [preview, setPreview] = useState<DryRun | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [started, setStarted] = useState(false);
  /** Which Stripe customer the account keeps, and whether the other is destroyed. */
  const [keep, setKeep] = useState<string | null>(null);
  const [removeOther, setRemoveOther] = useState(false);
  const [typed, setTyped] = useState('');

  const history = useQuery({
    queryKey: ['admin-migrations', accountId],
    queryFn: () => api.get<LedgerRow[]>(`/admin/accounts/${accountId}/migrations`),
    // While a live run is in flight there is nothing to watch but the ledger it writes at the end.
    refetchInterval: started ? 5_000 : false,
  });

  const dryRun = useMutation({
    mutationFn: () => api.post<DryRun>(`/admin/accounts/${accountId}/migrate`, {
      email: email.trim(),
      ...(cognitoId.trim() ? { cognitoId: cognitoId.trim() } : {}),
      ...(stripeCustomerId.trim() ? { stripeCustomerId: stripeCustomerId.trim() } : {}),
      mode: 'dry-run',
    }),
    onSuccess: (r) => {
      setErr(null);
      setPreview(r);
      // Default to the customer that actually holds subscriptions; with two empty ones, keep the account's
      // own. Never preselect a deletion — that has to be an act, not an accepted default.
      const legacy = r.stripe?.legacy;
      const current = r.stripe?.current;
      const best = (legacy?.subscriptions.length ?? 0) > (current?.subscriptions.length ?? 0) ? legacy : current;
      setKeep(best?.customerId ?? current?.customerId ?? legacy?.customerId ?? null);
      setRemoveOther(false);
      setTyped('');
    },
    onError: (e) => { setPreview(null); setErr(e instanceof ApiError ? e.message : 'The preview failed'); },
  });

  const both = preview?.stripe?.twoCustomers ? [preview.stripe.current, preview.stripe.legacy].filter(Boolean) as CustomerSummary[] : [];
  const other = both.find((c) => c.customerId !== keep) ?? null;

  const [outcome, setOutcome] = useState<{ purged?: { customerId: string; canceled: number }; warnings?: string[] } | null>(null);

  const run = useMutation({
    mutationFn: () =>
      api.post<{ started: boolean; purged?: { customerId: string; canceled: number }; warnings?: string[] }>(`/admin/accounts/${accountId}/migrate`, {
        email: email.trim(),
        ...(cognitoId.trim() ? { cognitoId: cognitoId.trim() } : {}),
        ...(stripeCustomerId.trim() ? { stripeCustomerId: stripeCustomerId.trim() } : {}),
        mode: 'live',
        ...(keep ? { keepCustomer: keep } : {}),
        ...(removeOther && other ? { removeCustomer: other.customerId, confirmCustomer: typed.trim() } : {}),
      }),
    onSuccess: (r) => {
      setErr(null);
      setConfirm(false);
      setStarted(true);
      setOutcome(r as { purged?: { customerId: string; canceled: number }; warnings?: string[] });
      qc.invalidateQueries({ queryKey: ['admin-migrations', accountId] });
    },
    onError: (e) => { setConfirm(false); setErr(e instanceof ApiError ? e.message : 'The migration could not be started'); },
  });

  const found = preview && movesData(preview.counts) > 0;

  return (
    <div className="panel">
      <div className="section-title" style={{ marginTop: 0 }}>Migrate v1 data into this account</div>
      <p className="cart-note" style={{ textAlign: 'left', marginTop: 0 }}>
        // Enter the customer's <strong>v1 (old) email address</strong>. Their transactions, locations,
        credits and subscriptions are copied into <strong>{accountName}</strong>. Re-running is safe — records
        are keyed by their v1 ids, so a repeat overwrites rather than duplicates.
      </p>

      <div className="migrate-row">
        <label className="field" style={{ flex: '1 1 260px' }}>
          <span className="field-label">v1 email address</span>
          <input
            className="input"
            type="email"
            placeholder="customer@example.com"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setPreview(null); setStarted(false); }}
          />
        </label>
        <button
          className="btn btn-ghost"
          disabled={dryRun.isPending || (email.trim().length < 5 && !cognitoId.trim() && !stripeCustomerId.trim())}
          onClick={() => { setErr(null); dryRun.mutate(); }}
        >
          {dryRun.isPending ? 'Checking…' : 'Preview'}
        </button>
      </div>

      {/* The escape hatch for the mismatch case. Hidden until asked for: the email works for almost everyone. */}
      <details className="migrate-alt" open={altOpen} onToggle={(e) => setAltOpen(e.currentTarget.open)}>
        <summary>The email doesn't find them?</summary>
        <p className="field-help" style={{ margin: '8px 0' }}>
          A v1 customer can have one address on their sign-in and a different one on their v1 record. Either of
          these finds them regardless — the v1 Cognito id, or the Stripe customer their v1 record points at.
        </p>
        <div className="migrate-row">
          <label className="field" style={{ flex: '1 1 240px' }}>
            <span className="field-label">v1 Cognito id (sub)</span>
            <input className="input" value={cognitoId} placeholder="45b7c41b-…"
              onChange={(e) => { setCognitoId(e.target.value); setPreview(null); }} />
          </label>
          <label className="field" style={{ flex: '1 1 200px' }}>
            <span className="field-label">v1 Stripe customer</span>
            <input className="input" value={stripeCustomerId} placeholder="cus_…"
              onChange={(e) => { setStripeCustomerId(e.target.value); setPreview(null); }} />
          </label>
        </div>
      </details>

      {err && <p className="cart-error">{err}</p>}

      {preview && (
        <div className={found ? 'banner-ok' : 'banner-warn'} style={{ marginTop: 12 }}>
          {found ? (
            <>
              <div style={{ marginBottom: 8 }}>
                Found a v1 account for <strong>{preview.email}</strong>. Records would be owned by{' '}
                <strong>{preview.userEmail ?? preview.userId}</strong>.
              </div>
              {/* Matching on anything but the email means the v1 data itself is inconsistent — worth saying out
                  loud, since it explains why the customer's first sign-in produced nothing. */}
              {preview.matchedOn && preview.matchedOn !== 'email' && (
                <div className="banner-warn" style={{ margin: '0 0 10px' }}>
                  Matched by <strong>{preview.matchedOn === 'cognitoId' ? 'v1 Cognito id' : 'Stripe customer'}</strong>, not
                  email — their v1 record is filed under{' '}
                  <strong>{preview.legacyEmail ?? 'no email'}</strong>. That mismatch is why their first sign-in
                  found nothing.
                </div>
              )}
              <div className="migrate-counts">
                {LABELS.map(([k, label]) => (
                  <div key={k} className="migrate-count">
                    <span className="migrate-count-num">{preview.counts[k]}</span>
                    <span className="migrate-count-lbl">{label}</span>
                  </div>
                ))}
              </div>
              {/* Devices are counted for context but never written — a device row carries no entitlement
                  and its scanner re-registers itself on first use. */}
              <div className="field-help" style={{ marginTop: 10 }}>
                Devices are listed for context only; scanners re-register themselves when next used.
              </div>
            </>
          ) : (
            <span>
              {preview.message === 'no legacy user for email'
                ? `No v1 account exists for ${preview.email}. Check the address — this is the OLD one they used in v1.`
                : `A v1 account was found for ${preview.email}, but it holds no records to migrate.`}
            </span>
          )}
        </div>
      )}

      {/*
        * Two Stripe customers: the v1 one holding their real billing history, and one created when they
        * signed up again. Only one can be attached — every webhook resolves an account from exactly one
        * customer id — so the choice has to be made, and it is made from what each one is actually billing
        * rather than from the ids.
        */}
      {found && both.length === 2 && (
        <div className="cust-choice">
          <div className="section-title" style={{ marginTop: 0 }}>Two Stripe customers — which does this account keep?</div>
          {both.map((c) => (
            <label key={c.customerId} className={`cust-choice-opt${keep === c.customerId ? ' is-picked' : ''}`}>
              <input type="radio" name="keep-customer" checked={keep === c.customerId}
                onChange={() => { setKeep(c.customerId); setRemoveOther(false); setTyped(''); }} />
              <div>
                <div className="cust-choice-head">
                  <span className="mono">{c.customerId}</span>
                  <span className="field-help">
                    {c.customerId === preview?.stripe.current?.customerId ? 'on this account' : 'from v1'}
                  </span>
                </div>
                <div className="field-help">{c.email ?? c.name ?? 'no email on file'}</div>
                {c.subscriptions.length === 0 ? (
                  <div className="field-help">No live subscriptions.</div>
                ) : (
                  <ul className="cust-choice-subs">
                    {c.subscriptions.map((sub) => (
                      <li key={sub.subscriptionId}>
                        {sub.planName ?? sub.subscriptionId}
                        {sub.interval ? ` · per ${sub.interval}` : ''} · <span className="mono">{sub.status}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </label>
          ))}

          {other && (
            <div className="cust-choice-remove">
              <label className="checkbox-row">
                <input type="checkbox" checked={removeOther}
                  onChange={(e) => { setRemoveOther(e.target.checked); setTyped(''); }} />
                <span>
                  Delete <span className="mono">{other.customerId}</span> from Stripe, canceling{' '}
                  {other.subscriptions.length} subscription{other.subscriptions.length === 1 ? '' : 's'} immediately.
                </span>
              </label>
              {removeOther && (
                <>
                  <p className="cart-error" style={{ margin: '8px 0' }}>
                    This cannot be undone. Deleting a Stripe customer removes their invoices, payment methods
                    and billing history, and stops any subscription above from renewing or being charged.
                  </p>
                  {/* Typing the id is the one confirmation that can't be satisfied without reading which
                      customer is about to be destroyed. */}
                  <label className="field">
                    <span className="field-label">Type <span className="mono">{other.customerId}</span> to confirm</span>
                    <input className="input" value={typed} onChange={(e) => setTyped(e.target.value)}
                      placeholder={other.customerId} spellCheck={false} />
                  </label>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {found && (
        <button
          className="btn btn-primary"
          style={{ marginTop: 12 }}
          disabled={removeOther && typed.trim() !== other?.customerId}
          onClick={() => setConfirm(true)}
        >
          Migrate into this account →
        </button>
      )}

      {started && (
        <>
          {outcome?.purged && (
            <div className="banner-warn" style={{ marginTop: 12 }}>
              Deleted <span className="mono">{outcome.purged.customerId}</span> from Stripe, canceling{' '}
              {outcome.purged.canceled} subscription{outcome.purged.canceled === 1 ? '' : 's'}.
            </div>
          )}
          {outcome?.warnings?.map((w) => (
            <div key={w} className="banner-ok" style={{ marginTop: 12 }}>{w}</div>
          ))}
          <p className="cart-note" style={{ textAlign: 'left', marginTop: 12 }}>
            // Migration started. It runs in the background — a large history takes a minute or two — and
            appears below when it finishes.
          </p>
        </>
      )}

      {(history.data?.length ?? 0) > 0 && (
        <>
          <div className="section-title">Previous migrations</div>
          <table className="tx-table">
            <thead><tr><th>From (v1)</th><th>Completed</th><th>Records</th><th>State</th></tr></thead>
            <tbody>
              {history.data!.map((r, i) => (
                <tr key={`${r.userId}-${i}`}>
                  <td>{r.legacyEmail ?? <span className="field-help">first login</span>}</td>
                  <td>{r.completedAt ? new Date(r.completedAt).toLocaleString() : '—'}</td>
                  <td>{r.counts ? movesData(r.counts) : '—'}</td>
                  <td>
                    {r.state ?? '—'}
                    {r.warnings?.map((w) => <div key={w} className="tx-fail-reason">{w}</div>)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <ConfirmDialog
        open={confirm}
        title="Migrate this v1 account?"
        confirmLabel="Migrate"
        busy={run.isPending}
        onCancel={() => setConfirm(false)}
        onConfirm={() => run.mutate()}
      >
        <p>
          Copying <strong>{preview ? movesData(preview.counts) : 0} records</strong> from{' '}
          <strong>{preview?.email}</strong> into <strong>{accountName}</strong>.
        </p>
        <p className="field-help">
          The customer will see these transactions as their own. Check the v1 address is the right one —
          there is no undo, and removing records afterwards means deleting them one by one.
        </p>
        {both.length === 2 ? (
          <>
            <p className="field-help">
              Billing stays with <strong className="mono">{keep}</strong>.
            </p>
            {removeOther && other && (
              <p className="cart-error">
                <strong className="mono">{other.customerId}</strong> will be deleted from Stripe and its{' '}
                {other.subscriptions.length} subscription{other.subscriptions.length === 1 ? '' : 's'} cancelled
                immediately. This cannot be undone.
              </p>
            )}
          </>
        ) : (
          <p className="field-help">
            Billing is not moved: if this account is already linked to its own Stripe customer, that link is
            left alone and the v1 subscriptions will not appear here.
          </p>
        )}
      </ConfirmDialog>
    </div>
  );
}
